import { defineNuxtPlugin } from 'nuxt/app'
import Vue from 'vue'
import type { IVueI18n } from 'vue-i18n'
import {
  formatRelative,
  intlFormat,
  formatDuration,
  formatDistanceToNow
} from 'date-fns'
import { formatFileSize } from '~/lib/utils/number'
import type { Plugin } from '~/plugins/plugins'

const dateFnsLocales = {
  da: () => import('date-fns/locale/da'),
  de: () => import('date-fns/locale/de'),
  en: () => import('date-fns/locale/en-GB'),
  es: () => import('date-fns/locale/es'),
  fr: () => import('date-fns/locale/fr'),
  it: () => import('date-fns/locale/it'),
  ko: () => import('date-fns/locale/ko'),
  nl: () => import('date-fns/locale/nl'),
  pl: () => import('date-fns/locale/pl'),
  pt: () => import('date-fns/locale/pt'),
  ru: () => import('date-fns/locale/ru'),
  ja: () => import('date-fns/locale/ja'),
  zh: () => import('date-fns/locale/zh-CN')
}

export const vueFiltersPlugin: Plugin = async (nuxtApp) => {
  const i18n: IVueI18n = nuxtApp.$i18n ?? nuxtApp.nuxt2Context.app.i18n
  let locale = await dateFnsLocales[
    i18n.locale as keyof typeof dateFnsLocales
  ]()
  i18n.onBeforeLanguageSwitch = (_oldLocale: string, newLocale: string) => {
    dateFnsLocales[newLocale as keyof typeof dateFnsLocales]().then(
      (newLocale) => {
        locale = newLocale
      }
    )
  }

  const filters = Object.assign(nuxtApp.$filters || {}, {
    parseInt: (val: string) => parseInt(val),
    date: (val: string) => filters.format(val),
    time: (val: string) => {
      const normalizedVal = normalizeDateInput(val)
      return filters.format(normalizedVal, {
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric'
      })
    },
    datetime: (val: string) => {
      const normalizedVal = normalizeDateInput(val)
      return filters.format(normalizedVal, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric'
      })
    },
    format: (
      val: string,
      format = {
        year: 'numeric' as const,
        month: 'numeric' as const,
        day: 'numeric' as const
      }
    ) => {
      try {
        const normalizedVal = normalizeDateInput(val)
        return intlFormat(new Date(normalizedVal), format, {
          locale: (locale.default || locale).code
        })
      } catch (err) {
        return val
      }
    },
    dateRelative: (val: string) => {
      try {
        const normalizedVal = normalizeDateInput(val)
        return formatRelative(new Date(normalizedVal), new Date(), {
          locale: locale.default || locale
        })
      } catch (err) {
        return val
      }
    },
    runtime(val: string, unit: string) {
      const normalizedUnit = /(\w+?)([sS]\b)/.test(unit) ? unit : `${unit}s`
      return formatDuration(
        {
          [normalizedUnit]: parseInt(val)
        },
        {
          format: [normalizedUnit],
          zero: true,
          locale: locale.default || locale
        }
      )
    },
    dateDiff(date: Date) {
      return formatDistanceToNow(date, {
        addSuffix: true,
        includeSeconds: false,
        locale: locale.default || locale
      })
    },
    lastSeen(val: string) {
      return `${nuxtApp.$i18n.t(
        'source_component.last_seen.label'
      )}: ${filters.dateRelative(val)}`
    },
    // Format bytes as human-readable text.
    humanFileSize(bytes: number) {
      if (bytes <= 0) return 0

      const { value, unit } = formatFileSize(bytes)
      return `${value} ${unit}`
    }
  })

  // add all methods as filters on each vue component
  Object.keys(filters).forEach((key) => {
    Vue.filter(key, filters[key])
  })

  function normalizeDateInput(val: string) {
    // fix date format when unix timestamp is given instead of iso date string
    if (
      typeof val === 'string' &&
      Array.isArray(val.match(/^(\d{10}|\d{13})$/))
    ) {
      if (val.length === 10) val += '000'
      return parseInt(val)
    }
    return val
  }

  Vue.prototype.$filters = filters
  nuxtApp.provide('filters', filters)
}

export default defineNuxtPlugin(vueFiltersPlugin)
